import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, InputGroup, Badge, Spinner, Alert, Collapse, OverlayTrigger, Tooltip, Nav } from 'react-bootstrap';
import RecommendationCard from './RecommendationCard';
import { FaSearch, FaFilter, FaMusic, FaChevronDown, FaChevronUp, FaMagic } from 'react-icons/fa';

import dataService from '../services/dataService';
import searchService from '../services/searchService';
import { formatRating, getRatingColor, getRatingValue } from '../utils/ratingUtils';
import { getGoogleSearchUrl, getYouTubeMusicUrl } from '../utils/linkUtils';
import { formatRelativeDate, parseGenres, getGenreBadgeVariant } from '../utils/dataUtils';
import RatingModal from './RatingModal';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/i18n';
import { SEARCH_LIST_LIMIT } from '../api/apiClient';
import { useLazyLoad } from '../hooks/useLazyLoad';
import { error as logError } from '../utils/logger';

const RecommendationsList = ({
  userPreferences,
  onMarkAsWatched,
  aiServiceStatus,
  initialSearch = '',
  initialCategory = '',
  controlledContentType,
  onContentTypeChange,
  showTypeTabs = true,
  embeddedInLibrary = false,
}) => {
  const { language } = useLanguage();
  const [recommendations, setRecommendations] = useState([]);
  const [filteredRecommendations, setFilteredRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [internalContentType, setInternalContentType] = useState(
    initialCategory && ['movie', 'series', 'music'].includes(initialCategory) ? initialCategory : 'movie'
  );
  const contentType = controlledContentType ?? internalContentType;
  const setContentType = onContentTypeChange ?? setInternalContentType;
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'omitted', 'all', 'favorites', 'favorites_active', 'favorites_omitted'
  const [genreFilter, setGenreFilter] = useState(''); // Género seleccionado para filtrar
  const [sortBy, setSortBy] = useState('default');
  const [favoriteLoading, setFavoriteLoading] = useState(new Set()); // IDs de recomendaciones siendo procesadas como favoritos
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  // Búsqueda externa (TMDB/Last.fm): películas/series/música para marcar como vistas
  const [searchQueryExternal, setSearchQueryExternal] = useState(initialSearch || '');
  const [searchGenreExternal, setSearchGenreExternal] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [generatingFirst, setGeneratingFirst] = useState(false);
  const [searchSectionOpen, setSearchSectionOpen] = useState(!!initialSearch);
  const needsFavoriteClientFilter = ['favorites', 'favorites_active', 'favorites_omitted'].includes(statusFilter);
  const needsClientListFilter = needsFavoriteClientFilter || Boolean(searchQuery.trim()) || Boolean(genreFilter);
  /** List already sorted by API when loading a single content type without client-only filters. */
  const skipClientSort = contentType !== 'all' && !needsClientListFilter;

  useEffect(() => {
    loadRecommendations();
    
    // Escuchar evento de actualización de recomendaciones
    const handleRecommendationsUpdated = () => {
      // Limpiar caché y recargar
      dataService.clearCache();
      loadRecommendations();
    };
    
    window.addEventListener('recommendationsUpdated', handleRecommendationsUpdated);
    return () => {
      window.removeEventListener('recommendationsUpdated', handleRecommendationsUpdated);
    };
  }, [contentType, statusFilter, sortBy, searchQuery, genreFilter]);

  useEffect(() => {
    filterAndSortRecommendations();
  }, [recommendations, searchQuery, contentType, statusFilter, genreFilter, sortBy, language]);

  // Lazy loading para las recomendaciones filtradas
  const { visibleItems, hasMore, loadingRef } = useLazyLoad(filteredRecommendations, 12, 12);

  const loadRecommendations = async () => {
    try {
      setLoading(true);
      setError(null);

      if (contentType !== 'all') {
        const res = await dataService.getRecommendationsPage(contentType, 1, {
          limit: SEARCH_LIST_LIMIT,
          statusFilter: needsFavoriteClientFilter ? 'all' : statusFilter,
          sort: sortBy
        });
        setRecommendations(res.items || []);
      } else {
        const loadedRecommendations = await dataService.getAllRecommendations();
        setRecommendations(loadedRecommendations);
      }
    } catch (err) {
      logError('Error loading recommendations:', err);
      setError(t('recommendations.errorLoading', language));
    } finally {
      setLoading(false);
    }
  };

  const handleFillWithAI = async () => {
    const targetContentType =
      contentType === 'all' ? 'movie' : contentType;
    setGeneratingFirst(true);
    setError(null);
    try {
      const success = await dataService.generateAutoRecommendations(targetContentType, 20, {
        force: true,
        language
      });
      if (success) {
        await loadRecommendations();
        setError(null);
      }
    } catch (err) {
      logError('Error filling recommendations with AI:', err);
      setError(t('recommendations.errorLoading', language));
    } finally {
      setGeneratingFirst(false);
    }
  };

  const handleGenerateFirstRecommendations = async () => {
    setGeneratingFirst(true);
    setError(null);
    try {
      // Determinar el tipo de contenido según el filtro seleccionado
      let targetContentType = 'movie'; // Por defecto películas
      if (contentType === 'series') {
        targetContentType = 'series';
      } else if (contentType === 'music') {
        targetContentType = 'music';
      } else if (contentType === 'all') {
        // Si está en "all", generar para películas primero
        targetContentType = 'movie';
      }
      
      // Generar recomendaciones para el tipo seleccionado
      const success = await dataService.generateAutoRecommendations(targetContentType, 20, {
        force: true,
        language
      });
      if (success) {
        await loadRecommendations();
        // Limpiar error si fue exitoso
        setError(null);
      } else {
        // El error ya fue mostrado en el LoadingModal, solo mostrar aquí si no hay modal
        const typeLabel = contentType === 'movie' 
          ? t('recommendations.typeMovies', language)
          : contentType === 'series' 
          ? t('recommendations.typeTVSeries', language)
          : t('recommendations.typeMusic', language);
        setError(tf('recommendations.couldNotGenerate', { type: typeLabel }, language));
      }
    } catch (err) {
      logError('Error generating first recommendations:', err);
      const typeLabel = contentType === 'movie' 
        ? t('recommendations.typeMovies', language)
        : contentType === 'series' 
        ? t('recommendations.typeTVSeries', language)
        : t('recommendations.typeMusic', language);
      setError(tf('recommendations.errorGenerating', { type: typeLabel }, language));
    } finally {
      setGeneratingFirst(false);
    }
  };

  const getWatchedContent = async () => {
    const [movies, series, music] = await Promise.all([
      dataService.getWatchedMovies(),
      dataService.getWatchedSeries(),
      dataService.getListenedMusic()
    ]);
    return [...movies, ...series, ...music];
  };

  // Obtener géneros únicos disponibles según el tipo de contenido
  const getAvailableGenres = () => {
    const filteredByType = contentType === 'all' 
      ? recommendations 
      : recommendations.filter(rec => rec.type === contentType);
    
    const genreSet = new Set();
    filteredByType.forEach(rec => {
      const genres = parseGenres(rec.genre);
      genres.forEach(g => genreSet.add(g.trim()));
    });
    
    return Array.from(genreSet).sort();
  };

  const getRecommendedAtMs = (rec) => {
    const raw = rec.recommended_at || rec.recommendedAt;
    if (!raw) return 0;
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const compareRecommendations = (a, b, mode) => {
    const locale = language === 'en' ? 'en' : 'es';
    switch (mode) {
      case 'date_desc':
        return getRecommendedAtMs(b) - getRecommendedAtMs(a);
      case 'date_asc':
        return getRecommendedAtMs(a) - getRecommendedAtMs(b);
      case 'rating_asc': {
        const scoreA = getRatingValue(a, 'average_rating') || 0;
        const scoreB = getRatingValue(b, 'average_rating') || 0;
        return scoreA - scoreB;
      }
      case 'rating_desc': {
        const scoreA = getRatingValue(a, 'average_rating') || 0;
        const scoreB = getRatingValue(b, 'average_rating') || 0;
        return scoreB - scoreA;
      }
      case 'external_desc': {
        const scoreA = getRatingValue(a, 'external_rating') || 0;
        const scoreB = getRatingValue(b, 'external_rating') || 0;
        return scoreB - scoreA;
      }
      case 'ai_desc': {
        const scoreA = getRatingValue(a, 'ai_rating') || 0;
        const scoreB = getRatingValue(b, 'ai_rating') || 0;
        return scoreB - scoreA;
      }
      case 'title_asc':
        return (a.title || '').localeCompare(b.title || '', locale, { sensitivity: 'base' });
      case 'default':
      default: {
        const isFavoriteA = a.is_favorite === true || a.favorite === true;
        const isFavoriteB = b.is_favorite === true || b.favorite === true;
        if (isFavoriteA && !isFavoriteB) return -1;
        if (!isFavoriteA && isFavoriteB) return 1;
        const scoreA = getRatingValue(a, 'average_rating') || 0;
        const scoreB = getRatingValue(b, 'average_rating') || 0;
        return scoreB - scoreA;
      }
    }
  };

  const getSortLabelKey = (value) => {
    const keys = {
      default: 'sortDefault',
      date_desc: 'sortDateDesc',
      date_asc: 'sortDateAsc',
      rating_desc: 'sortRatingDesc',
      rating_asc: 'sortRatingAsc',
      external_desc: 'sortExternalDesc',
      ai_desc: 'sortAiDesc',
      title_asc: 'sortTitleAsc'
    };
    return keys[value] || 'sortDefault';
  };

  const filterAndSortRecommendations = () => {
    let filtered = [...recommendations];

    // Filtrar por estado y favoritos combinados
    if (statusFilter === 'active') {
      filtered = filtered.filter(rec => (rec.status || 'active') === 'active');
    } else if (statusFilter === 'omitted') {
      filtered = filtered.filter(rec => (rec.status || 'active') === 'omitted');
    } else if (statusFilter === 'favorites') {
      filtered = filtered.filter(rec => rec.is_favorite === true || rec.favorite === true);
    } else if (statusFilter === 'favorites_active') {
      filtered = filtered.filter(rec => 
        (rec.is_favorite === true || rec.favorite === true) && 
        (rec.status || 'active') === 'active'
      );
    } else if (statusFilter === 'favorites_omitted') {
      filtered = filtered.filter(rec => 
        (rec.is_favorite === true || rec.favorite === true) && 
        (rec.status || 'active') === 'omitted'
      );
    }
    // Si es 'all', no filtramos por estado

    // Filtrar por tipo de contenido
    if (contentType !== 'all') {
      filtered = filtered.filter(rec => rec.type === contentType);
    }

    // Filtrar por género
    if (genreFilter) {
      filtered = filtered.filter(rec => {
        const genres = parseGenres(rec.genre);
        return genres.some(g => 
          g.toLowerCase().includes(genreFilter.toLowerCase())
        );
      });
    }

    // Filtrar por búsqueda
    if (searchQuery) {
      filtered = filtered.filter(rec => 
        rec.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (rec.artist && rec.artist.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (rec.actors && rec.actors.some(actor => 
          actor.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }

    if (!skipClientSort) {
      filtered.sort((a, b) => compareRecommendations(a, b, sortBy));
    }

    setFilteredRecommendations(filtered);
  };

  const handleMarkAsWatched = (content) => {
    setSelectedContent(content);
    setShowRatingModal(true);
  };

  const handleToggleFavorite = async (rec, e) => {
    e.stopPropagation();
    const currentFavorite = rec.is_favorite || rec.favorite || false;
    const newFavorite = !currentFavorite;
    
    // Actualizar estado local inmediatamente para feedback visual
    setRecommendations(prev => prev.map(r => 
      r.id === rec.id 
        ? { ...r, is_favorite: newFavorite, favorite: newFavorite }
        : r
    ));
    
    // Marcar como cargando
    setFavoriteLoading(prev => new Set(prev).add(rec.id));
    
    const contentType = rec.type || 'movie';
    const success = await dataService.setRecommendationFavorite(contentType, rec.id, newFavorite);
    
    // Quitar de carga
    setFavoriteLoading(prev => {
      const next = new Set(prev);
      next.delete(rec.id);
      return next;
    });
    
    if (!success) {
      // Si falló, revertir el cambio
      setRecommendations(prev => prev.map(r => 
        r.id === rec.id 
          ? { ...r, is_favorite: currentFavorite, favorite: currentFavorite }
          : r
      ));
    }
  };

  const handleOmitRecommendation = async (rec) => {
      if (window.confirm(t('recommendations.confirmOmit', language).replace('{title}', rec.title))) {
      const success = await dataService.setRecommendationStatus(rec.type, rec.id, 'omitted');
      if (success) {
        await loadRecommendations();
      }
    }
  };

  const handleReactivateRecommendation = async (rec) => {
    const success = await dataService.setRecommendationStatus(rec.type, rec.id, 'active');
    if (success) {
      await loadRecommendations();
    }
  };

  const handleRatingSubmit = async (rating) => {
    if (selectedContent) {
      const contentToMark = {
        ...selectedContent,
        type: selectedContent.type || (selectedContent.id?.startsWith('lastfm_') ? 'music' : contentType)
      };
      const success = await onMarkAsWatched(contentToMark, rating);
      if (success) {
        setShowRatingModal(false);
        setSelectedContent(null);
        if (selectedContent.id && (selectedContent.id.startsWith('tmdb_') || selectedContent.id.startsWith('lastfm_'))) {
          setSearchResults(prev => prev.filter(r => r.id !== selectedContent.id));
        } else {
          setRecommendations(prev => prev.filter(rec => rec.id !== selectedContent.id));
        }
        await loadRecommendations();
      }
    }
  };

  const handleSearchExternal = async () => {
    const q = searchQueryExternal.trim();
    const genre = searchGenreExternal.trim();
    if (contentType === 'music') {
      if (!q && !genre) {
        setSearchError(t('recommendations.enterSongOrGenre', language));
        return;
      }
    } else if (!q || (contentType !== 'movie' && contentType !== 'series')) {
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    try {
      let results = [];
      if (contentType === 'movie') {
        results = await searchService.searchMovies(q);
      } else if (contentType === 'series') {
        results = await searchService.searchSeries(q);
      } else if (contentType === 'music') {
        results = await searchService.searchMusic(q, genre);
      }
      setSearchResults(results);
    } catch (err) {
      setSearchError(err.message || t('recommendations.errorSearching', language));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const isNewRecommendation = (rec) => {
    const dateStr = rec.recommended_at || rec.recommendedAt;
    if (!dateStr) return false;
    const recDate = new Date(dateStr);
    if (isNaN(recDate.getTime())) return false;
    const now = new Date();
    const diffMs = now - recDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours < 24;
  };

  const renderRecommendationCard = (rec) => {
    const isFavorite = rec.is_favorite === true || rec.favorite === true;
    return (
      <Col key={rec.id} xs={12} sm={6} md={4} className="mb-4">
        <RecommendationCard
          rec={rec}
          language={language}
          isFavorite={isFavorite}
          favoriteLoading={favoriteLoading.has(rec.id)}
          onToggleFavorite={handleToggleFavorite}
          onMarkAsWatched={handleMarkAsWatched}
          onOmit={handleOmitRecommendation}
          onReactivate={handleReactivateRecommendation}
          isNew={isNewRecommendation(rec)}
        />
      </Col>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t('recommendations.loading', language)}</p>
      </div>
    );
  }

  const renderAdvancedFilters = () => (
    <>
      <Row className="g-2">
        <Col xs={12} md={4}>
          <Form.Select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            aria-label={t('recommendations.allGenresOption', language)}
          >
            <option value="">{t('recommendations.allGenresOption', language)}</option>
            {getAvailableGenres().map((genre) => (
              <option key={genre} value={genre}>{genre}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={12} md={4}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="active">{t('recommendations.active', language)}</option>
            <option value="omitted">{t('recommendations.omitted', language)}</option>
            <option value="favorites">{t('recommendations.favorites', language)}</option>
            <option value="favorites_active">{t('recommendations.favoritesActive', language)}</option>
            <option value="favorites_omitted">{t('recommendations.favoritesOmitted', language)}</option>
            <option value="all">{t('recommendations.all', language)}</option>
          </Form.Select>
        </Col>
        <Col xs={12} md={4}>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label={t('recommendations.sortBy', language)}
          >
            <option value="default">{t('recommendations.sortDefault', language)}</option>
            <option value="date_desc">{t('recommendations.sortDateDesc', language)}</option>
            <option value="date_asc">{t('recommendations.sortDateAsc', language)}</option>
            <option value="rating_desc">{t('recommendations.sortRatingDesc', language)}</option>
            <option value="rating_asc">{t('recommendations.sortRatingAsc', language)}</option>
            <option value="external_desc">{t('recommendations.sortExternalDesc', language)}</option>
            <option value="ai_desc">{t('recommendations.sortAiDesc', language)}</option>
            <option value="title_asc">{t('recommendations.sortTitleAsc', language)}</option>
          </Form.Select>
        </Col>
      </Row>
      {(genreFilter || sortBy !== 'default') && (
        <Row className="mt-2">
          <Col>
            <div className="d-flex align-items-center flex-wrap gap-2">
              {genreFilter && (
                <Badge bg="info" className="d-flex align-items-center gap-1">
                  {t('recommendations.filteringBy', language)}: {genreFilter}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => setGenreFilter('')}
                    aria-label={t('recommendations.clearFilter', language)}
                  />
                </Badge>
              )}
              {sortBy !== 'default' && (
                <Badge bg="secondary" className="d-flex align-items-center gap-1">
                  {t('recommendations.sortBy', language)}: {t(`recommendations.${getSortLabelKey(sortBy)}`, language)}
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => setSortBy('default')}
                    aria-label={t('recommendations.clearFilter', language)}
                  />
                </Badge>
              )}
            </div>
          </Col>
        </Row>
      )}
    </>
  );

  return (
    <div className={embeddedInLibrary ? 'recommendations-list--embedded' : ''}>
      {showTypeTabs && (
        <Nav variant="tabs" className="recommendations-type-tabs mb-3">
          {[
            { key: 'movie', label: t('recommendations.movies', language) },
            { key: 'series', label: t('recommendations.series', language) },
            { key: 'music', label: t('recommendations.music', language) },
          ].map((tab) => (
            <Nav.Item key={tab.key}>
              <Nav.Link
                active={contentType === tab.key}
                onClick={() => {
                  setContentType(tab.key);
                  setGenreFilter('');
                }}
              >
                {tab.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
      )}

      <div
        className={`recommendations-filters-sticky-wrap mb-4 ${
          embeddedInLibrary ? 'recommendations-filters-sticky-wrap--library' : ''
        }`}
      >
      <Card className="miskosas-card border-0 shadow-sm mb-0">
        <Card.Body>
          <Row>
            <Col xs={12}>
              <InputGroup className="mb-2 mb-md-3">
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('recommendations.searchPlaceholder', language)}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
            </Col>
          </Row>
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-md-none mb-2 w-100"
            onClick={() => setFiltersExpanded((v) => !v)}
            aria-expanded={filtersExpanded}
          >
            <FaFilter className="me-1" />
            {filtersExpanded
              ? t('recommendations.hideFilters', language)
              : t('recommendations.showFilters', language)}
          </Button>
          <div className="d-none d-md-block">{renderAdvancedFilters()}</div>
          <Collapse in={filtersExpanded} className="d-md-none">
            <div className="pt-1">{renderAdvancedFilters()}</div>
          </Collapse>
        </Card.Body>
      </Card>
      </div>

      {/* Buscador externo: películas/series/música para marcar como vistas (TMDB/Last.fm) - colapsable */}
      {(contentType === 'movie' || contentType === 'series') && (
        <Card className="mb-4">
          <Card.Header
            className="py-2 px-3 d-flex align-items-center justify-content-between cursor-pointer"
            onClick={() => setSearchSectionOpen(!searchSectionOpen)}
            style={{ cursor: 'pointer' }}
            aria-expanded={searchSectionOpen}
          >
            <span className="h6 mb-0">
              <FaSearch className="me-2" />
              {t('recommendations.searchMoviesTitle', language).replace('{type}', contentType === 'movie' ? t('recommendations.typeMovies', language) : t('recommendations.typeTVSeries', language))}
            </span>
            {searchSectionOpen ? <FaChevronUp /> : <FaChevronDown />}
          </Card.Header>
          <Collapse in={searchSectionOpen}>
            <Card.Body>
              <p className="small text-muted mb-3">
                {t('recommendations.searchMoviesDescription', language)}
              </p>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  placeholder={contentType === 'movie' 
                    ? t('recommendations.movieNamePlaceholder', language)
                    : t('recommendations.seriesNamePlaceholder', language)}
                  value={searchQueryExternal}
                  onChange={(e) => setSearchQueryExternal(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchExternal()}
                />
                <Button
                  variant="primary"
                  onClick={handleSearchExternal}
                  disabled={searchLoading}
                >
                  {searchLoading ? <Spinner animation="border" size="sm" /> : <><FaSearch className="me-1" /> {t('common.search', language)}</>}
                </Button>
              </InputGroup>
              {searchError && <Alert variant="warning" className="mb-0 mt-2 py-2">{searchError}</Alert>}
              {searchResults.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-muted mb-2">{t('recommendations.results', language)} ({searchResults.length})</h6>
                  <Row>
                    {searchResults.map((item) => (
                      <Col key={item.id} lg={6} xl={4} className="mb-3">
                        <Card className="h-100 border">
                          <Card.Body className="py-3">
                            <div className="d-flex align-items-start">
                              {item.poster_path && (
                                <img
                                  src={item.poster_path}
                                  alt=""
                                  style={{ width: 48, height: 72, objectFit: 'cover', borderRadius: 4 }}
                                  className="me-2"
                                />
                              )}
                              <div className="flex-grow-1 min-w-0">
                                <Card.Title className="h6 mb-1">{item.title}</Card.Title>
                                <Badge bg="secondary" className="me-1">{item.year || '—'}</Badge>
                                {parseGenres(item.genre).map((g) => (
                                  <Badge key={g} bg={getGenreBadgeVariant(g)} className="me-1 mb-1">
                                    {g}
                                  </Badge>
                                ))}
                                {item.description && (
                                  <p
                                    className="small text-muted mt-1 mb-0 text-break"
                                    style={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      wordBreak: 'break-word'
                                    }}
                                    title={item.description}
                                  >
                                    {item.description}
                                  </p>
                                )}
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => { setSelectedContent(item); setShowRatingModal(true); }}
                                >
                                  {t('recommendations.markAsWatched', language)}
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Card.Body>
          </Collapse>
        </Card>
      )}

      {/* Buscador de música (Last.fm) - colapsable */}
      {contentType === 'music' && (
        <Card className="mb-4">
          <Card.Header
            className="py-2 px-3 d-flex align-items-center justify-content-between cursor-pointer"
            onClick={() => setSearchSectionOpen(!searchSectionOpen)}
            style={{ cursor: 'pointer' }}
            aria-expanded={searchSectionOpen}
          >
            <span className="h6 mb-0">
              <FaMusic className="me-2" />
              {t('recommendations.searchMusicTitle', language)}
            </span>
            {searchSectionOpen ? <FaChevronUp /> : <FaChevronDown />}
          </Card.Header>
          <Collapse in={searchSectionOpen}>
            <Card.Body>
              <p className="small text-muted mb-3">
                {t('recommendations.searchMusicDescription', language)}
              </p>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Control
                    type="text"
                    placeholder={t('recommendations.songOrArtistPlaceholder', language)}
                    value={searchQueryExternal}
                    onChange={(e) => setSearchQueryExternal(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearchExternal()}
                  />
                </Col>
                <Col md={4}>
                  <Form.Select
                    value={searchGenreExternal}
                    onChange={(e) => setSearchGenreExternal(e.target.value)}
                  >
                    <option value="">{t('recommendations.allGenresOption', language)}</option>
                    <option value="cumbia">Cumbia</option>
                    <option value="cumbia argentina">Cumbia Argentina</option>
                    <option value="reggaeton">Reggaeton</option>
                    <option value="rock">Rock</option>
                    <option value="pop">Pop</option>
                    <option value="romantic">Romántica</option>
                    <option value="latin">Latina</option>
                    <option value="salsa">Salsa</option>
                    <option value="bachata">Bachata</option>
                    <option value="merengue">Merengue</option>
                    <option value="vallenato">Vallenato</option>
                    <option value="ranchera">Ranchera</option>
                    <option value="mariachi">Mariachi</option>
                    <option value="flamenco">Flamenco</option>
                    <option value="indie">Indie</option>
                    <option value="electronic">Electrónica</option>
                    <option value="jazz">Jazz</option>
                    <option value="blues">Blues</option>
                    <option value="country">Country</option>
                    <option value="hip hop">Hip Hop</option>
                    <option value="rap">Rap</option>
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="primary"
                    onClick={handleSearchExternal}
                    disabled={searchLoading}
                    className="w-100"
                  >
                    {searchLoading ? <Spinner animation="border" size="sm" /> : <><FaSearch className="me-1" /> {t('common.search', language)}</>}
                  </Button>
                </Col>
              </Row>
              {searchError && <Alert variant="warning" className="mb-0 mt-2 py-2">{searchError}</Alert>}
              {searchResults.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-muted mb-2">{t('recommendations.results', language)} ({searchResults.length})</h6>
                  <Row>
                    {searchResults.map((item) => (
                      <Col key={item.id} lg={6} xl={4} className="mb-3">
                        <Card className="h-100 border">
                          <Card.Body className="py-3">
                            <div className="d-flex align-items-start">
                              {item.poster_path && (
                                <img
                                  src={item.poster_path}
                                  alt=""
                                  style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4 }}
                                  className="me-2"
                                />
                              )}
                              <div className="flex-grow-1 min-w-0">
                                <Card.Title className="h6 mb-1">{item.title}</Card.Title>
                                {item.artist && (
                                  <Card.Subtitle className="small text-muted mb-1">{item.artist}</Card.Subtitle>
                                )}
                                {parseGenres(item.genre).map((g) => (
                                  <Badge key={g} bg={getGenreBadgeVariant(g)} className="me-1 mb-1">
                                    {g}
                                  </Badge>
                                ))}
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => { setSelectedContent(item); setShowRatingModal(true); }}
                                >
                                  {t('recommendations.markAsListened', language)}
                                </Button>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </Card.Body>
          </Collapse>
        </Card>
      )}

      {/* Resultados */}
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h4 className="mb-0">
          {t('recommendations.title', language)} ({filteredRecommendations.length})
        </h4>
        <div className="d-flex flex-wrap gap-2 ms-auto">
          {filteredRecommendations.length > 0 && (
            <Button
              className="btn-miskosas-primary library-generate-ai-btn"
              size="sm"
              onClick={handleFillWithAI}
              disabled={loading || generatingFirst}
            >
              {generatingFirst ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  {t('recommendations.generating', language)}
                </>
              ) : (
                <>
                  <FaMagic className="me-1" />
                  {t('recommendations.fillWithAI', language)}
                </>
              )}
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={loadRecommendations}
            disabled={loading}
          >
            <FaFilter className="me-1" />
            {t('recommendations.refresh', language)}
          </Button>
        </div>
      </div>

      {filteredRecommendations.length === 0 ? (
        (() => {
          // Verificar si hay recomendaciones del tipo seleccionado
          const hasRecommendationsForType = contentType === 'all' 
            ? recommendations.length > 0
            : recommendations.some(rec => rec.type === contentType);
          
          if (!hasRecommendationsForType) {
            // No hay recomendaciones del tipo seleccionado, mostrar botón para generarlas
            const typeLabel = contentType === 'movie' 
              ? t('recommendations.typeMovies', language)
              : contentType === 'series'
              ? t('recommendations.typeTVSeries', language)
              : contentType === 'music'
              ? t('recommendations.typeMusic', language)
              : t('common.noData', language);
            
            return (
              <Alert variant="info" className="text-center py-5">
                <h5 className="mb-3">
                  {recommendations.length === 0 
                    ? t('recommendations.welcomeNoRecommendations', language)
                    : t('recommendations.noRecommendationsOfType', language).replace('{type}', typeLabel)
                  }
                </h5>
                <p className="mb-4">
                  {recommendations.length === 0 
                    ? t('recommendations.generateFirst', language)
                    : t('recommendations.generateType', language).replace('{type}', typeLabel)
                  }
                </p>
                <Button
                  className="btn-miskosas-primary"
                  size="lg"
                  onClick={handleGenerateFirstRecommendations}
                  disabled={generatingFirst}
                >
                  {generatingFirst ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t('recommendations.generating', language)}
                    </>
                  ) : (
                    recommendations.length === 0
                      ? t('recommendations.generateFirstButton', language)
                      : t('recommendations.generateTypeButton', language).replace('{type}', typeLabel)
                  )}
                </Button>
                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </Alert>
            );
          } else {
            // Hay recomendaciones pero los filtros las ocultan
            return (
              <Alert variant="info">
                {t('recommendations.noResultsWithFilters', language)}
              </Alert>
            );
          }
        })()
      ) : (
        <>
          <Row>
            {visibleItems.map(renderRecommendationCard)}
          </Row>
          {hasMore ? (
            <div ref={loadingRef} className="text-center py-4">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">{t('common.loading', language)}</span>
              </Spinner>
            </div>
          ) : null}
        </>
      )}

      {/* Modal de calificación */}
      <RatingModal
        show={showRatingModal}
        onHide={() => setShowRatingModal(false)}
        content={selectedContent}
        onSubmit={handleRatingSubmit}
      />
    </div>
  );
};

export default RecommendationsList;
